import { useState, useEffect } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Search, Heart, MessageCircle, Plus, CreditCard } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";
import { useParentCredits } from "@/hooks/useParentCredits";
import { ShopMessageThread } from "@/components/ShopMessageThread";
import { PaymentMethodSelector } from "@/components/PaymentMethodSelector";
import { useNavigate } from "react-router-dom";

export default function Marketplace() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [sellerFilter, setSellerFilter] = useState("all");
  const [items, setItems] = useState<any[]>([]);
  const [categories, setCategories] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedItem, setSelectedItem] = useState<any>(null);
  const [ptaId, setPtaId] = useState<string | null>(null);
  const [showMessages, setShowMessages] = useState(false);
  const [showPaymentSelector, setShowPaymentSelector] = useState(false);
  const [shopSettings, setShopSettings] = useState<any>(null);
  const { balance } = useParentCredits(ptaId || undefined);

  useEffect(() => {
    fetchPtaId();
  }, [user]);

  useEffect(() => {
    if (ptaId) {
      fetchCategories();
      fetchItems();
      fetchShopSettings();
    }
  }, [ptaId]);

  const fetchShopSettings = async () => {
    if (!ptaId) return;
    const { data } = await supabase
      .from('shop_settings')
      .select('*')
      .eq('pta_id', ptaId)
      .single();
    
    if (data) setShopSettings(data);
  };

  const fetchPtaId = async () => {
    if (!user) return;
    
    const { data: memberData } = await supabase
      .from('members')
      .select('pta_id')
      .eq('user_id', user.id)
      .single();

    if (memberData) {
      setPtaId(memberData.pta_id);
    } else {
      const { data: parentData } = await supabase
        .from('parents')
        .select('pta_id')
        .eq('user_id', user.id)
        .single();
      
      if (parentData) {
        setPtaId(parentData.pta_id);
      }
    }
  };

  const fetchCategories = async () => {
    if (!ptaId) return;
    
    const { data } = await supabase
      .from('shop_categories')
      .select('*')
      .eq('pta_id', ptaId)
      .order('display_order');
    
    setCategories(data || []);
  };

  const fetchItems = async () => {
    if (!ptaId) return;
    
    setLoading(true);
    const { data, error } = await supabase
      .from('shop_items')
      .select(`
        *,
        seller_parent:parents!seller_parent_id(full_name),
        pta:ptas(name)
      `)
      .eq('pta_id', ptaId)
      .eq('status', 'active')
      .eq('approval_status', 'approved')
      .or(`expires_at.is.null,expires_at.gt.${new Date().toISOString()}`)
      .order('created_at', { ascending: false });

    if (error) {
      toast({
        title: "Error loading items",
        description: error.message,
        variant: "destructive"
      });
    } else {
      setItems(data || []);
    }
    setLoading(false);
  };

  const filteredItems = items.filter(item => {
    const matchesSearch = item.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         (item.description?.toLowerCase().includes(searchQuery.toLowerCase()) || false);
    const matchesCategory = categoryFilter === "all" || item.category === categoryFilter;
    const matchesSeller = sellerFilter === "all" || item.seller_type === sellerFilter;
    return matchesSearch && matchesCategory && matchesSeller;
  });

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-3xl font-bold">Marketplace</h1>
          <p className="text-muted-foreground mt-2">Buy and sell items within your PTA community</p>
        </div>
        
        {user && (
          <div className="flex gap-2 items-center">
            {balance && (
              <Card className="px-4 py-2">
                <div className="flex items-center gap-2">
                  <CreditCard className="h-4 w-4 text-primary" />
                  <div className="text-sm">
                    <div className="font-semibold">{balance.credits_available} Credits</div>
                    {balance.credits_pending > 0 && (
                      <div className="text-xs text-muted-foreground">
                        {balance.credits_pending} pending
                      </div>
                    )}
                  </div>
                </div>
              </Card>
            )}
            <Button onClick={() => navigate('/marketplace/buy-credits')} variant="outline" size="sm">
              Buy Credits
            </Button>
            <Button onClick={() => navigate('/marketplace/my-listings')} variant="outline" size="sm">
              My Listings
            </Button>
            <Button onClick={() => navigate('/marketplace/create')} size="sm">
              <Plus className="h-4 w-4 mr-2" />
              Sell Item
            </Button>
          </div>
        )}
      </div>

      <div className="flex gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[300px]">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search items..."
            className="pl-9"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        <Select value={categoryFilter} onValueChange={setCategoryFilter}>
          <SelectTrigger className="w-48">
            <SelectValue placeholder="Category" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Categories</SelectItem>
            {categories.map(cat => (
              <SelectItem key={cat.id} value={cat.name}>{cat.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={sellerFilter} onValueChange={setSellerFilter}>
          <SelectTrigger className="w-48">
            <SelectValue placeholder="Seller Type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Sellers</SelectItem>
            <SelectItem value="pta">PTA Items</SelectItem>
            <SelectItem value="parent">Parent Items</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="text-center py-12">
          <p className="text-muted-foreground">Loading items...</p>
        </div>
      ) : filteredItems.length === 0 ? (
        <div className="text-center py-12">
          <p className="text-muted-foreground">No items found</p>
        </div>
      ) : (
        <div className="grid md:grid-cols-3 lg:grid-cols-4 gap-4">
          {filteredItems.map((item) => (
            <Card 
              key={item.id} 
              className="cursor-pointer hover:shadow-lg transition-shadow"
              onClick={() => setSelectedItem(item)}
            >
              <CardContent className="p-4">
                <div className="aspect-square relative mb-3 bg-muted rounded-lg overflow-hidden">
                  {item.images && item.images.length > 0 ? (
                    <img 
                      src={item.images[0]} 
                      alt={item.name} 
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-6xl">
                      📦
                    </div>
                  )}
                </div>
                <h3 className="font-semibold text-sm mb-1 line-clamp-2">{item.name}</h3>
                {item.price && (
                  <p className="text-lg font-bold text-primary mb-1">£{parseFloat(item.price).toFixed(2)}</p>
                )}
                <div className="flex items-center gap-2 mb-2">
                  <Badge variant={item.seller_type === 'pta' ? 'default' : 'secondary'} className="text-xs">
                    {item.seller_type === 'pta' ? 'PTA' : item.seller_parent?.full_name}
                  </Badge>
                  <Badge variant="outline" className="text-xs">{item.category}</Badge>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {selectedItem && (
        <Dialog open={!!selectedItem} onOpenChange={(open) => !open && setSelectedItem(null)}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{selectedItem.name}</DialogTitle>
              <DialogDescription>
                <div className="flex gap-2 mt-2">
                  <Badge variant={selectedItem.seller_type === 'pta' ? 'default' : 'secondary'}>
                    {selectedItem.seller_type === 'pta' ? `PTA - ${selectedItem.pta?.name}` : `Parent - ${selectedItem.seller_parent?.full_name}`}
                  </Badge>
                  <Badge variant="outline">{selectedItem.category}</Badge>
                </div>
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4">
              {selectedItem.images && selectedItem.images.length > 0 && (
                <div className="grid grid-cols-3 gap-2">
                  {selectedItem.images.map((img: string, idx: number) => (
                    <img 
                      key={idx}
                      src={img} 
                      alt={`${selectedItem.name} ${idx + 1}`}
                      className="w-full aspect-square object-cover rounded-lg"
                    />
                  ))}
                </div>
              )}
              
              <div>
                <h4 className="font-semibold mb-2">Description</h4>
                <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                  {selectedItem.description || 'No description provided'}
                </p>
              </div>

              {selectedItem.price && (
                <div>
                  <h4 className="font-semibold mb-2">Price</h4>
                  <p className="text-2xl font-bold text-primary">£{parseFloat(selectedItem.price).toFixed(2)}</p>
                </div>
              )}

              {selectedItem.contact_method && (
                <div>
                  <h4 className="font-semibold mb-2">Contact Details</h4>
                  <p className="text-sm">{selectedItem.contact_method}</p>
                </div>
              )}

              {user && (
                <div className="space-y-2">
                  {selectedItem.seller_type === 'pta' && selectedItem.price && (
                    <Button 
                      className="w-full"
                      onClick={() => setShowPaymentSelector(true)}
                    >
                      <CreditCard className="h-4 w-4 mr-2" />
                      Pay £{parseFloat(selectedItem.price).toFixed(2)}
                    </Button>
                  )}
                  <div className="flex gap-2">
                    <Button 
                      className="flex-1"
                      variant={selectedItem.seller_type === 'pta' ? 'outline' : 'default'}
                      onClick={() => setShowMessages(true)}
                    >
                      <MessageCircle className="h-4 w-4 mr-2" />
                      Message Seller
                    </Button>
                    <Button variant="outline" size="icon">
                      <Heart className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>
      )}

      {showMessages && selectedItem && user && (
        <ShopMessageThread
          itemId={selectedItem.id}
          itemName={selectedItem.name}
          open={showMessages}
          onOpenChange={setShowMessages}
        />
      )}

      {showPaymentSelector && selectedItem && shopSettings && (
        <PaymentMethodSelector
          open={showPaymentSelector}
          onOpenChange={setShowPaymentSelector}
          shopSettings={shopSettings}
          itemName={selectedItem.name}
          itemPrice={selectedItem.price ? parseFloat(selectedItem.price) : 0}
          onPaymentMethodSelected={(method) => {
            console.log("Selected payment method:", method);
            // TODO: Handle payment flow when Stripe is integrated
            toast({
              title: "Payment Method Selected",
              description: `${method} payment will be processed when Stripe is integrated.`
            });
          }}
        />
      )}
    </div>
  );
}
